import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, CheckCircle2, Clock, Shield, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import { services } from "../data/services";
import { locations } from "../data/locations";
import { projects } from "../data/projects";
import { canonical } from "../data/business";
import { IMG } from "../data/images";
import TransformationsCarousel from "../components/TransformationsCarousel";
import QuoteForm from "../components/QuoteForm";
import GrassOfferBanner from "../components/GrassOfferBanner";
import FaqAccordion from "../components/FaqAccordion";
import { faqs } from "../data/faqs";

export default function Home() {
  const faqJsonLd = { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) };

  return (
    <div className="page-enter">
      <Seo title="Last Cut Landscaping — Premium Landscapers Scotland | Edinburgh, Glasgow, Falkirk" description="Scotland's premium landscaping company. Artificial grass, patios, decking, fencing, garden design & full garden makeovers across the Central Belt. Free quotes · Fully insured." canonical={canonical("/")} jsonLd={faqJsonLd} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-cream" data-testid="hero">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-20 pb-16 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 fade-up">
            <div className="pill mb-5"><MapPin size={12} /> Scotland's Central Belt</div>
            <h1 className="text-display font-display text-[clamp(2.6rem,6vw,5.5rem)] font-medium text-dark">
              Gardens worth<br/>
              <span className="italic text-[color:var(--lc-green)]">coming home</span> to.
            </h1>
            <p className="mt-6 text-lg text-dark/75 max-w-xl">Premium landscaping across Edinburgh, Glasgow, Falkirk & the Central Belt of Scotland. Artificial grass, patios, decking, fencing & full garden makeovers — built to last decades.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote" className="btn-accent text-base px-7 py-4 shadow-lg shadow-lime-400/30 hover:scale-[1.02]" data-testid="hero-quote-btn">Get Your Free Quote <ArrowRight size={18}/></Link>
              <Link to="/projects" className="btn-ghost" data-testid="hero-projects-btn">See our work</Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-dark/70">
              <div className="flex items-center gap-2"><Shield size={16} className="text-[color:var(--lc-green)]"/> £5m insured</div>
              <div className="flex items-center gap-2"><Award size={16} className="text-[color:var(--lc-green)]"/> 2-year workmanship guarantee</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[color:var(--lc-green)]"/> 100% authentic project photos</div>
            </div>
          </div>
          <div className="lg:col-span-6 fade-up" style={{ animationDelay: ".15s" }}>
            <div className="aspect-[4/3] lg:aspect-[5/4] rounded-3xl overflow-hidden shadow-xl shadow-emerald-900/10">
              <img
                src={IMG.patioDining}
                alt="Artificial grass and patio garden makeover by Last Cut Landscaping"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                width="1600"
                height="1280"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 60%" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* PROMO OFFER BANNER (Artificial Grass) — directly below hero, after trust points */}
      <GrassOfferBanner />

      {/* MARQUEE LOCATION STRIP */}
      <section className="bg-dark text-white py-5 overflow-hidden relative noise" data-testid="location-marquee">
        <div className="marquee-track">
          {[...locations, ...locations].map((l, i) => (
            <Link key={i} to={`/locations/${l.slug}`} className="flex items-center gap-3 px-8 text-lg font-display hover:text-lime">
              <MapPin size={16} className="text-lime"/> Landscaping {l.name} <span className="text-white/30 ml-3">·</span>
            </Link>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-3">What we do</div>
              <h2 className="font-display text-4xl lg:text-6xl text-dark">Seven services.<br/><span className="italic">One trusted team.</span></h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 text-dark/70 text-lg">Everything outside your back door — from a small artificial grass install to a complete garden transformation. One contractor, fully insured, finished to a standard you'll be proud of.</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="lc-card group" data-testid={`service-card-${s.slug}`}>
                <div className="aspect-[4/3] overflow-hidden"><img src={s.image} alt={s.name + " Scotland"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-xl text-dark">{s.name}</h3>
                    <ArrowRight size={16} className="text-dark/40 group-hover:text-dark group-hover:translate-x-1 transition" />
                  </div>
                  <p className="text-sm text-dark/65 mt-2 line-clamp-2">{s.short}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER — top 10 most dramatic transformations (full set on /projects) */}
      <TransformationsCarousel items={[
        { before: IMG.deckBefore1, after: IMG.deckAfter1, title: "Deck & Artificial Grass Transformation", subtitle: "Premium decking, lush artificial lawn" },
        { before: IMG.frontCurbBefore, after: IMG.frontCurbAfter, title: "Front Garden Curb Appeal", subtitle: "Premium turf and block edging — instant kerb lift" },
        { before: IMG.widePanoramaBefore, after: IMG.widePanoramaAfter, title: "Large Garden Overhaul — Panorama", subtitle: "Full-scale wide-shot transformation" },
        { before: IMG.stonePatioBefore, after: IMG.stonePatioAfter, title: "Stone Patio & Lush Lawn", subtitle: "Premium paving paired with a fresh new lawn" },
        { before: IMG.terraceInProgress, after: IMG.restaurantTerrace, title: "Commercial Terrace Deck", subtitle: "In-progress build through to finished commercial install" },
        { before: IMG.slopedSideBefore, after: IMG.slopedSideAfter, title: "Sloped Side-Garden Transformation", subtitle: "Awkward slope reclaimed as usable space" },
        { before: IMG.backDeckBefore, after: IMG.backDeckAfter, title: "Back Garden Decking & Artificial Grass", subtitle: "Full back-garden overhaul" },
        { before: IMG.fencedStoneBefore, after: IMG.fencedStoneAfter, title: "Fenced Garden with Stone Edging", subtitle: "Crisp lines, premium materials" },
        { before: IMG.houseSideBefore, after: IMG.houseSideAfter, title: "House Side Lawn Transformation", subtitle: "Unused side strip reclaimed as garden space" },
        { before: IMG.blueSlideBefore, after: IMG.blueSlideAfter, title: "Deck, Blue Slide & New Lawn", subtitle: "Family-friendly garden with playful feature" },
      ]} />

      {/* OUR WORK GALLERY — multi-category showcase */}
      <section className="py-16 lg:py-24" data-testid="recent-gallery">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
            <div>
              <div className="eyebrow mb-3">Our work</div>
              <h2 className="font-display text-3xl lg:text-5xl text-dark">Patios, putting greens,<br/><span className="italic text-[color:var(--lc-green)]">decking & turf.</span></h2>
              <p className="text-dark/65 mt-3 text-base max-w-xl">Real Scottish gardens across our full range of services — including the unedited starting points. We're proud to show the full picture.</p>
            </div>
            <Link to="/projects" className="btn-ghost self-start lg:self-end">All projects <ArrowRight size={16}/></Link>
          </div>

          {/* Hero feature + 4 categorised tiles */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] lg:col-span-2 lg:row-span-2 lg:aspect-auto group">
              <img src={IMG.largeDeckTurf} alt="Large premium deck and artificial turf area installed by Last Cut Landscaping" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-4 left-4 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Decking & Turf</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.patioDining} alt="Premium stone patio with modern dining set — Last Cut Landscaping" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Patios</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.puttingGreenLuxury} alt="Luxury putting green with integrated flags — premium golf-grade turf install" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Putting Greens</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.patioCurved} alt="Stone patio with curved edging by Last Cut Landscaping" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Patios</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.deckTopiary} alt="Wooden deck with outdoor dining and topiary planting — Last Cut Landscaping" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Decking</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.familyStonePath} alt="Family garden with stone path, vibrant artificial lawn and playhouse" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Family Living</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.playgroundTurf} alt="Kids playhouse with dedicated playground artificial turf area" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Pet & Kid Safe</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.hangingBaskets} alt="Garden with hanging baskets and stone slab paving — Last Cut Landscaping" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Garden Design</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.longLawnNewFence} alt="Long rectangular lawn with brand-new fencing — premium finish by Last Cut Landscaping" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Lawn & Fencing</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.restaurantTerrace} alt="Commercial terrace deck with premium artificial turf" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Commercial</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.whiteGravelTurf} alt="Premium artificial turf with crisp white gravel border" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Artificial Grass</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.messyBeforeSlabs} alt="Real before — overgrown garden with old paving slabs, before Last Cut Landscaping transformation" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Real Before</div>
            </div>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] group">
              <img src={IMG.redDoorShed} alt="Premium artificial grass with bespoke red door shed feature" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
              <div className="absolute top-3 left-3 pill bg-white/90 text-dark border-0 backdrop-blur-sm">Artificial Grass</div>
            </div>
          </div>
        </div>
      </section>


      {/* TRUST COUNTERS */}
      <section className="py-16 lg:py-20 bg-dark text-white relative noise overflow-hidden" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {[
            { n: 2, l: "year workmanship guarantee", suffix: "yr" },
            { n: 5, l: "public liability insurance", suffix: "m" },
            { n: 7, l: "specialist services", suffix: "" },
            { n: 13, l: "Central Belt locations", suffix: "" },
          ].map((c, i) => (
            <div key={i} className="text-center lg:text-left" data-testid={`stat-${i}`}>
              <div className="font-display text-5xl lg:text-7xl text-lime">{c.n}<span className="text-white/40 text-3xl">{c.suffix}</span></div>
              <div className="text-sm text-white/65 mt-2 uppercase tracking-wider">{c.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROJECTS — hidden while photos are refreshed */}
      {projects.length > 0 && (
      <section className="py-20 lg:py-28" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="eyebrow mb-3">Recent projects</div>
              <h2 className="font-display text-4xl lg:text-5xl text-dark">Real gardens, real results.</h2>
            </div>
            <Link to="/projects" className="hidden md:inline-flex btn-ghost">All projects</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.slice(0, 6).map((p) => (
              <Link key={p.slug} to={`/projects/${p.slug}`} className="lc-card group" data-testid={`project-card-${p.slug}`}>
                <div className="aspect-[4/3] overflow-hidden"><img src={p.after} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/></div>
                <div className="p-5">
                  <div className="pill mb-2">{p.location}</div>
                  <h3 className="font-display text-lg text-dark">{p.title}</h3>
                  <p className="text-sm text-dark/65 mt-1.5 line-clamp-2">{p.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* WHY US */}
      <section className="py-20 lg:py-28 bg-paper" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Why Last Cut</div>
            <h2 className="font-display text-4xl lg:text-5xl text-dark">Built to a standard,<br/><span className="italic">not a price.</span></h2>
            <p className="mt-5 text-dark/70 text-lg">We're not the cheapest landscapers in Scotland — and we're glad. We use proper sub-bases, branded materials and the kind of attention to detail that's still there in 15 years.</p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { i: <Shield/>, h: "£5m insured", p: "Fully insured, CHAS-accredited and DBS-checked teams." },
              { i: <Award/>, h: "2-year guarantee", p: "Every project backed by a written 2-year workmanship warranty." },
              { i: <Clock/>, h: "On time, on budget", p: "Fixed-price quotes, scheduled milestones, no nasty surprises." },
              { i: <CheckCircle2/>, h: "Premium materials only", p: "Marshalls, Bradstone, Namgrass, Millboard — branded for a reason." },
            ].map((b, i) => (
              <div key={i} className="lc-card p-6">
                <div className="w-11 h-11 rounded-xl bg-dark text-lime grid place-items-center mb-3">{b.i}</div>
                <div className="font-display text-xl text-dark">{b.h}</div>
                <div className="text-dark/65 text-sm mt-1.5">{b.p}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28" data-testid="faq-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">Frequently asked</div>
            <h2 className="font-display text-4xl lg:text-5xl text-dark">Your questions, answered.</h2>
          </div>
          <FaqAccordion items={faqs} />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-dark text-white relative noise overflow-hidden" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6">
            <h2 className="font-display text-4xl lg:text-6xl">Let's build the garden<br/><span className="italic text-lime">you actually want.</span></h2>
            <p className="mt-5 text-white/75 text-lg max-w-lg">Tell us a little about your project. Free, no-obligation quote within 24 hours.</p>
          </div>
          <div className="lg:col-span-6">
            <QuoteForm sourcePage="home-cta" />
          </div>
        </div>
      </section>
    </div>
  );
}
